import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { staffAPI } from '../services/api';
import toast from '../utils/toast';

const invalidateTravelNotices = (queryClient) =>
  queryClient.invalidateQueries({ queryKey: ['travel-notice'] });

const mutationError = (error) =>
  toast.error(error?.response?.data?.error || 'Travel notice action failed.');

export const useTravelNoticeBootstrap = (options = {}) =>
  useQuery({
    queryKey: ['travel-notice', 'bootstrap'],
    queryFn: async () => {
      const response = await staffAPI.getTravelNoticeBootstrap();
      return response.data;
    },
    staleTime: 2 * 60 * 1000,
    ...options,
  });

export const useTravelNoticeWindowPreview = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['travel-notice', 'window-preview', params],
    queryFn: async () => {
      const response = await staffAPI.getTravelNoticeWindowPreview(params);
      return response.data;
    },
    staleTime: 30 * 1000,
    enabled: !!params?.departure_at && !!params?.expected_return_at,
    ...options,
  });

export const useMyActiveTravelNotices = (options = {}) =>
  useQuery({
    queryKey: ['travel-notice', 'my-active'],
    queryFn: async () => {
      const response = await staffAPI.getMyActiveTravelNotices();
      return response.data || [];
    },
    staleTime: 30 * 1000,
    ...options,
  });

export const useTravelNotices = (params = {}, options = {}) =>
  useQuery({
    queryKey: ['travel-notice', 'list', params],
    queryFn: async () => {
      const response = await staffAPI.getTravelNotices(params);
      return response.data || [];
    },
    staleTime: 30 * 1000,
    ...options,
  });

export const useTravelNoticeConcurrentAbsence = (params, options = {}) =>
  useQuery({
    queryKey: ['travel-notice', 'concurrent-absence', params],
    queryFn: async () => {
      const response = await staffAPI.getTravelNoticeConcurrentAbsence(params);
      return response.data || [];
    },
    staleTime: 60 * 1000,
    enabled: !!params?.start_date && !!params?.end_date,
    ...options,
  });

export const useCreateTravelNotice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data) => staffAPI.createTravelNotice(data),
    onSuccess: () => {
      invalidateTravelNotices(queryClient);
      toast.success('Travel notice submitted.');
    },
    onError: mutationError,
  });
};

export const useUpdateTravelNotice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) => staffAPI.updateTravelNotice(id, data),
    onSuccess: () => {
      invalidateTravelNotices(queryClient);
      toast.success('Travel notice updated.');
    },
    onError: mutationError,
  });
};

export const useCancelTravelNotice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => staffAPI.cancelTravelNotice(id),
    onSuccess: () => {
      invalidateTravelNotices(queryClient);
      toast.success('Travel notice cancelled.');
    },
    onError: mutationError,
  });
};

export const useConfirmTravelNoticeReturn = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => staffAPI.confirmTravelNoticeReturn(id),
    onSuccess: () => {
      invalidateTravelNotices(queryClient);
      toast.success('Return confirmed. Welcome back.');
    },
    onError: mutationError,
  });
};
